import { REACT_APP_INTERCOM_ENABLED } from '../../utils/config';
import { AccountResponse } from '../../models/account/AccountResponse';
import { IMPERSONATION_ORIGINAL_TOKEN_KEY } from '../../constans/constans';
import { sendIntercomOnboardingEvent } from '../../api/services/intercom/IntercomService';
import { TOKEN_KEY } from '../auth/AuthService';
import type { IntercomEventName } from './intercomEvents';

interface TrackIntercomEventOptions {
  projectId?: number;
}

// Two pings per event burst: Intercom evaluates checklist rules with no SLA,
// so a fast ping (snappy UX) is backed by a late one that catches slow
// evaluations. Still far below the 20 updates / 30 min page limit.
const CHECKLIST_REFRESH_DELAYS_MS = [1000, 8000];
const pendingOnceEvents = new Set<string>();
// One timer set is enough: only one contact identity is booted per page (an
// account switch reloads the page, which clears any pending timers anyway).
let checklistRefreshTimeoutIds: ReturnType<typeof setTimeout>[] = [];

/**
 * Presence check, deliberately NOT an equality check: the api client rewrites
 * the stored token on a routine 401 refresh, and that must not cancel a
 * pending Messenger ping. Within one page lifetime the identity can only
 * change via logout (token removed) or a full reload (timers die with it).
 */
const hasActiveSession = (): boolean => {
  try {
    return localStorage.getItem(TOKEN_KEY) !== null;
  } catch {
    return false;
  }
};

/**
 * True when this session's events should reach Intercom: a real, logged-in
 * customer — not a superadmin browsing someone's project and not an admin
 * impersonating a client (original admin token parked in sessionStorage).
 */
const isTrackableUser = (account: AccountResponse | null): account is AccountResponse => {
  if (!account || account.isSuperAdmin || account.isLimitedSuperAdmin) {
    return false;
  }
  try {
    return !sessionStorage.getItem(IMPERSONATION_ORIGINAL_TOKEN_KEY);
  } catch {
    return false;
  }
};

const fireChecklistRefresh = (account: AccountResponse): void => {
  // Re-checked at fire time: logout empties the token, impersonation may have
  // started while the timer was pending, and the Messenger may not be loaded.
  if (!hasActiveSession() || !isTrackableUser(account) || typeof window.Intercom !== 'function') {
    return;
  }

  try {
    // `update` simulates a page refresh in an SPA. Identity Verification
    // requires the user_id and its matching HMAC on every authenticated ping.
    window.Intercom('update', {
      user_id: account.intercomUserId,
      user_hash: account.intercomUserHash,
      last_request_at: Math.floor(Date.now() / 1000),
    });
  } catch {
    // The event is already accepted; a Messenger refresh must never resend it.
  }
};

const scheduleChecklistRefresh = (account: AccountResponse): void => {
  if (!hasActiveSession()) {
    return;
  }

  // Each call restarts both timers, so the pings fire 1s and 8s after the
  // LAST accepted event — Events API acceptance (202) precedes rule
  // evaluation, and the restart gives every event that grace period.
  checklistRefreshTimeoutIds.forEach(clearTimeout);
  checklistRefreshTimeoutIds = CHECKLIST_REFRESH_DELAYS_MS.map((delay) =>
    setTimeout(() => fireChecklistRefresh(account), delay)
  );
};

/**
 * Re-ping the currently booted Messenger after Intercom has had time to
 * process a backend event. Overlapping calls collapse into a single delayed
 * ping pair, keeping usage far below Intercom's 20 updates / 30 min page limit.
 */
export const scheduleIntercomChecklistRefresh = (account: AccountResponse | null): void => {
  if (!REACT_APP_INTERCOM_ENABLED || !isTrackableUser(account)) {
    return;
  }
  scheduleChecklistRefresh(account);
};

/**
 * Submit an onboarding event through the authenticated backend. The browser
 * marks it sent only after Intercom's Events API accepted it server-side.
 *
 * Intercom aggregates events into count + first/last seen, and checklist step
 * completion is monotonic — so re-firing the same event is safe.
 */
export const trackIntercomEvent = async (
  account: AccountResponse | null,
  name: IntercomEventName,
  options?: TrackIntercomEventOptions
): Promise<boolean> => {
  if (!REACT_APP_INTERCOM_ENABLED || !isTrackableUser(account)) {
    return false;
  }

  try {
    await sendIntercomOnboardingEvent({ eventName: name, projectId: options?.projectId });
  } catch {
    return false;
  }

  scheduleChecklistRefresh(account);
  return true;
};

/**
 * Fire an Intercom event at most once per user-account contact (per browser).
 * The v2 key intentionally ignores old frontend-only guards, which may have
 * been written even when the SDK request later failed.
 *
 * Resolves once the send attempt settles (immediately when skipped or already
 * pending) and never rejects — callers that navigate the page away can await
 * it so the POST is not aborted mid-flight.
 */
export const trackIntercomEventOnce = (
  account: AccountResponse | null,
  name: IntercomEventName
): Promise<void> => {
  if (!isTrackableUser(account)) {
    return Promise.resolve();
  }
  const key = `intercom_evt_v2_${account.intercomUserId}_${name}`;
  try {
    if (localStorage.getItem(key)) {
      return Promise.resolve();
    }
  } catch {
    // localStorage unavailable (private mode / quota) — fire without the guard.
  }

  if (pendingOnceEvents.has(key)) {
    return Promise.resolve();
  }
  pendingOnceEvents.add(key);

  return trackIntercomEvent(account, name)
    .then((sent) => {
      if (!sent) {
        return;
      }
      try {
        localStorage.setItem(key, '1');
      } catch {
        // Guard not persisted; monotonic events tolerate re-firing.
      }
    })
    .finally(() => {
      pendingOnceEvents.delete(key);
    });
};
